// The camera director: address the ball, follow the flight, settle back. OrbitControls stay live for
// the hand — if the player grabs the view, the director stands down for a second and a half. In XR
// none of this runs; the rig is the camera there (see xr.ts standAt).

import * as THREE from 'three';
import { sampleHeight, type Hole } from '@shared/course';
import type { World } from './world';
import type { Ball } from './ball';

export interface Director {
  readonly mode: 'address' | 'follow';
  address(hole: Hole, pos: { x: number; z: number }, aimDeg: number, instant?: boolean): void;
  follow(ball: Ball, hole: Hole): void;
  tickFollow(ball: Ball, hole: Hole): void;
  snap(): void;
  update(): void;
}

export function createDirector(world: World): Director {
  const { camera, controls } = world;
  const wantPos = new THREE.Vector3();
  const wantTgt = new THREE.Vector3();
  let mode: 'address' | 'follow' = 'address';
  let userHeld = false;
  let lerp = 0.12;

  controls.addEventListener('start', () => { userHeld = true; });
  controls.addEventListener('end', () => { setTimeout(() => { userHeld = false; }, 1600); });

  const director: Director = {
    get mode() { return mode; },

    // Behind the ball and nearly level, so the hole and the sky are both in the frame.
    address(hole, pos, aimDeg, instant = false) {
      mode = 'address';
      const a = (aimDeg * Math.PI) / 180;
      const y = sampleHeight(hole, pos.x, pos.z);
      wantTgt.set(pos.x + Math.sin(a) * 30, y + 2.6, pos.z + Math.cos(a) * 30);
      wantPos.set(pos.x - Math.sin(a) * 9, y + 3.1, pos.z - Math.cos(a) * 9);
      lerp = instant ? 1 : 0.1;
      if (instant) director.snap();
    },

    follow(ball, hole) {
      mode = 'follow';
      director.tickFollow(ball, hole);
    },

    // Trail the ball from behind and above, leading the frame toward where it is going.
    tickFollow(ball, hole) {
      const p = ball.position;
      const g = sampleHeight(hole, p.x, p.z);
      const h = Math.max(0, p.y - g);
      const back = 11 + h * 0.5;
      const up = 2.2 + h * 0.3;
      const end = ball.samples.length > 2 ? ball.samples[ball.samples.length - 1] : null;
      const ax = end ? Math.atan2(end.x - p.x, end.z - p.z) : 0;
      wantPos.set(p.x - Math.sin(ax) * back, Math.max(g + 2.6, p.y + up), p.z - Math.cos(ax) * back);
      wantTgt.set(p.x + Math.sin(ax) * 14, p.y + 1.4, p.z + Math.cos(ax) * 14);
      lerp = 0.06;
    },

    snap() {
      camera.position.copy(wantPos);
      controls.target.copy(wantTgt);
      controls.update();
    },

    update() {
      if (userHeld) return;
      camera.position.lerp(wantPos, lerp);
      controls.target.lerp(wantTgt, Math.min(1, lerp * 1.6));
      controls.update();
    },
  };
  return director;
}
